import {
  SegmentControlTabs,
  DocketDisplayItemType,
  DocketHeaderColor,
  Scale,
} from './types';
import {
  DocketStatus,
  LayoutStrategy,
  OrderTypeCode,
} from '@oolio-group/domain';
import theme from './theme';

export const RUSH_TAG = 'RUSH';

export const TAGS = [
  { tag: '🍳', label: 'Claimed' },
  { tag: '🔥', label: 'High Priority' },
  { tag: '⏸️', label: 'On Hold' },
  { tag: '⚠️', label: 'Issue Flagged' },
  { tag: '🚀', label: 'Fast Track' },
  { tag: '📦', label: 'Packed' },
  { tag: '✅', label: 'Final Step' },
  { tag: '⏳', label: 'Waiting' },
  { tag: '🤬', label: 'Guest Issue' },
  { tag: '⭐️', label: 'VIP Order' },
  { tag: '🎁', label: 'Gift Order' },
  { tag: '🧨', label: 'Urgent Priority' },
  { tag: '🍽️', label: 'Ready to Serve' },
  { tag: '🔪', label: 'Prep Stage' },
  { tag: '🌶️', label: 'Extra Spicy' },
  { tag: '❄️', label: 'Frozen' },
];

export const ORDER_TYPES_STORAGE_KEY = 'ORDER_TYPES';
export const SALES_CHANNEL_STORAGE_KEY = 'SALES_CHANNEL';
export const ORG_TIMINGS_STORAGE_KEY = 'ORG_TIMINGS';
export const CATEGORIES_STORAGE_KEY = 'CATEGORIES';

export const LOG_ROCKET_ID = 'ypqfwn/kds-app';
export const LOG_ROCKET_TRACKING_BUZA_MC_ORG_ID =
  '28064-23da-40c2-9915-9630466efcbb';

export const DELAY_TO_BUMP = 500;
export const DEVICE_CODE_LENGTH = 8;
export const SECONDS_IN_TWO_HOUR = 60 * 60 * 2;

export const DOCKETS = 'DOCKETS';
export const DOCKET_TITLE_BAR = 'DOCKET TITLE BAR';
export const CONTINUE = 'CONTINUE';
export const VOIDED_TEXT = 'VOIDED';
export const RECALL_TEXT = 'RECALL';
export const RECALLED_TEXT = 'RECALLED';
export const UNASSIGNED_COURSE = 'Unassigned';

export const IOS_SCALE = 0.8;
export const LOGO_RATIO = 0.26;
export const DOCKET_WIDTH = 294;
export const DOCKET_HEIGHT = 294;
export const INTERFACE_HEIGHT = 176;

export const DEFAULT_ROWS_PER_PAGE: Record<LayoutStrategy, number> = {
  [LayoutStrategy.FREE_FLOW]: 1,
  [LayoutStrategy.SINGLE_ROW]: 1,
  [LayoutStrategy.DOUBLE_ROW]: 2,
  [LayoutStrategy.GRID]: 2, //default value,
};

export const GRID_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

/** Translation keys for the display name of each layout strategy. */
export const LAYOUT_NAME_KEYS: Record<LayoutStrategy, string> = {
  [LayoutStrategy.SINGLE_ROW]: 'kitchenDisplay.singleRowLayout',
  [LayoutStrategy.DOUBLE_ROW]: 'kitchenDisplay.doubleRowLayout',
  [LayoutStrategy.FREE_FLOW]: 'kitchenDisplay.freeFlowLayout',
  [LayoutStrategy.GRID]: 'kitchenDisplay.gridLayout',
};

/** Translation keys for the one-line description of each layout strategy. */
export const LAYOUT_DESCRIPTION_KEYS: Record<LayoutStrategy, string> = {
  [LayoutStrategy.SINGLE_ROW]: 'kitchenDisplay.singleRowLayoutDesc',
  [LayoutStrategy.DOUBLE_ROW]: 'kitchenDisplay.doubleRowLayoutDesc',
  [LayoutStrategy.FREE_FLOW]: 'kitchenDisplay.freeFlowLayoutDesc',
  [LayoutStrategy.GRID]: 'kitchenDisplay.gridLayoutDesc',
};

/**
 * Layout strategy lookups that tolerate an unrecognised value. A persisted
 * strategy from an older release is typed as a `LayoutStrategy` but need not be
 * one of the members these maps cover, and indexing directly would hand
 * `undefined` to `translate`. Free Flow is the app-wide default, so it stands
 * in for anything unknown.
 */
export const getLayoutNameKey = (strategy: LayoutStrategy): string =>
  LAYOUT_NAME_KEYS[strategy] ?? LAYOUT_NAME_KEYS[LayoutStrategy.FREE_FLOW];

export const getLayoutDescriptionKey = (strategy: LayoutStrategy): string =>
  LAYOUT_DESCRIPTION_KEYS[strategy] ??
  LAYOUT_DESCRIPTION_KEYS[LayoutStrategy.FREE_FLOW];

export const DefaultReceiveMessage =
  'Hi {{customer}}, your {{orderType}} order at {{store}} is confirmed. You can view your receipt here: {{receiptLink}}.';
export const DefaultBumpMessage =
  'Hi {{customer}}, your {{orderType}} order at {{store}} is ready to be picked up. You can view your receipt here: {{receiptLink}}.';

export enum CourseStatus {
  AWAY = 'AWAY',
  HOLD = 'HOLD',
}

export enum ScaleFactor {
  SMALL = 1,
  MEDIUM = 1.12,
  LARGE = 1.25,
}

export const TEXT_SIZE_OPTIONS = [
  { value: Scale.SMALL, label: 'Small' },
  { value: Scale.MEDIUM, label: 'Medium' },
  { value: Scale.LARGE, label: 'Large' },
];

// Define the order of headers - items earlier in the array should appear before items later in the array
export const headerOrder = [
  DocketDisplayItemType.DOCKET_TITLE_BAR,
  DocketDisplayItemType.TRAINING_DOCKET,
  DocketDisplayItemType.DOCKET_HEADER,
  // Add XYZ_HEADER here in the future
];

// Define the headers that are meant to be sticky when docket is scrollable
export const stickyHeaders = [
  DocketDisplayItemType.DOCKET_TITLE_BAR,
  DocketDisplayItemType.TRAINING_DOCKET,
  DocketDisplayItemType.DOCKET_HEADER,
  DocketDisplayItemType.CONTINUED,
  // Add XYZ_HEADER here in the future
];

export const DocketDisplayItemTypeHeight = {
  [DocketDisplayItemType.DOCKET_TITLE_BAR]: 28,
  [DocketDisplayItemType.DOCKET_HEADER]: 80,
  [DocketDisplayItemType.TRAINING_DOCKET]: 42,
  [DocketDisplayItemType.VOID]: 54,
  [DocketDisplayItemType.DUE_AT]: 54,
  [DocketDisplayItemType.ORDER_NOTE]: 54,
  [DocketDisplayItemType.RECALLED_TAG]: 54,
  [DocketDisplayItemType.COURSE]: 54,
  [DocketDisplayItemType.ITEM]: 53,
  [DocketDisplayItemType.ITEM + 'NOTE']: 26,
  [DocketDisplayItemType.ITEM + 'MODIFIER']: 26,
  [DocketDisplayItemType.ITEM + 'VARIANT']: 26,
  [DocketDisplayItemType.CONTINUE]: 38,
  [DocketDisplayItemType.CONTINUED]: 38,
  [DocketDisplayItemType.RECALL_BUTTON]: 45,
  [DocketDisplayItemType.TRANSFERRED_TO_LABEL]: 54,
  [DocketDisplayItemType.TRANSFERRED_FROM_LABEL]: 54,
  [DocketDisplayItemType.SEAT_NUMBER]: 54,
  [DocketDisplayItemType.CATEGORY]: 54,
  [DocketDisplayItemType.OTHER_ITEMS]: 44,
};

export const options: SegmentControlTabs[] = [
  {
    label: 'Open',
    value: DocketStatus.CREATED,
  },
  {
    label: 'Closed',
    value: DocketStatus.COMPLETED,
  },
  {
    label: 'Hold',
    value: CourseStatus.HOLD,
  },
  {
    label: 'Away',
    value: CourseStatus.AWAY,
  },
];

export const DOCKET_HEADER_COLOR: DocketHeaderColor = {
  [OrderTypeCode.DELIVERY]: theme.colors.blue,
  [OrderTypeCode.PICK_UP]: theme.colors.blue,
  [OrderTypeCode.TAKEAWAY]: theme.colors.blue,
  [OrderTypeCode.CATERING]: theme.colors.deepPurple,
  [OrderTypeCode.DINE_IN]: theme.colors.deepPurple,
};

// REPLACE
export const FooterHeight = 76;
export const HeaderHeight = 76;
export const TextLineHeight = 20;
export const TextCharacterCount = 28;

export enum SortingPreference {
  NEWEST_FIRST = 'NEWEST_FIRST',
  OLDEST_FIRST = 'OLDEST_FIRST',
  TABLE_NAME = 'TABLE_NAME',
}

export enum FEATURES {
  DOCKETS_RECEIVED_ACK = 'DocketReceivedAck',
  AUTO_BUMP_FEATURE = 'AutoBumpFeature',
}

export enum DocketPlacement {
  START = 'start',
  BACKWARD = 'backward',
  FORWARD = 'forward',
  END = 'end',
}
